import React, { MouseEvent, useEffect } from 'react';
import { Button, Card, CardContent, Typography, Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { LOYALTY_APPLY_STATUS } from '../../reducers/cartSlice.constants';
import {
  messagingActions,
  InfoTransmissionMessage,
} from '../../reducers/messagingSlice';
import { useAppDispatch } from '../../app/hooks';
import LoyaltyApplyStatus from './LoyaltyApplyStatus';
import { CartItemAdditionTypes } from '../../constants/event';
import Colors from '../../utils/color';
import { LoyaltyResponseStatus } from '../../reducers/orderSlice.constants';

interface LoyaltiesItemReviewProps {
  code: string;
  addedBy: CartItemAdditionTypes;
  status: LoyaltyResponseStatus;
  handleLoyaltyRemove: (code: string) => void;
}

interface ILoyaltiesItemReviewStyleProps {
  addedBy: CartItemAdditionTypes;
}

const useStyles = makeStyles<Theme, ILoyaltiesItemReviewStyleProps>(
  ({ spacing }: Theme) => ({
    container: {
      marginTop: spacing(2),
      cursor: 'pointer',
      backgroundColor: ({ addedBy }) =>
        addedBy === CartItemAdditionTypes.AI
          ? `${Colors.aiItemIndicator} !important`
          : `${Colors.white} !important`,
    },
    content: {
      paddingBottom: `${spacing(0.5)} !important`,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    codeStyle: {
      marginBottom: `${spacing(0.625)} !important`,
      fontWeight: 'bold !important',
    },
    buttonStyle: {
      fontWeight: 'bold !important',
      '&.MuiButtonBase-root.MuiButton-root': {
        textTransform: 'none',
      },
    },
  })
);

const NOTFOUND_ERROR = 'Loyalty code not found';

const LoyaltiesItemReview = ({
  code,
  addedBy,
  status,
  handleLoyaltyRemove,
}: LoyaltiesItemReviewProps) => {
  const dispatch = useAppDispatch();
  const { container, content, codeStyle, buttonStyle } = useStyles({ addedBy });

  const removeLoyalty = (e: MouseEvent) => {
    e.stopPropagation();
    handleLoyaltyRemove(code);
  };

  useEffect(() => {
    if (status === LoyaltyResponseStatus.valid) {
      const payload: Partial<InfoTransmissionMessage> = {
        data: {
          message: 'Loyalty code applied successfully',
          type: 'METRIC',
          code: 'LOYALTY_APPLIED',
          metadata: { loyalty_code: code },
        },
      };
      dispatch(messagingActions.sendInfo(payload as any));
    }
  }, [status, code, dispatch]);

  const statusType = (() => {
    switch (status) {
      case LoyaltyResponseStatus.sending:
        return LOYALTY_APPLY_STATUS.SENDING;
      case LoyaltyResponseStatus.pending:
        return LOYALTY_APPLY_STATUS.PROCESSING;
      case LoyaltyResponseStatus.valid:
        return LOYALTY_APPLY_STATUS.SUCCESS;
      case LoyaltyResponseStatus.invalid:
        return LOYALTY_APPLY_STATUS.INVALID;
      default:
        return LOYALTY_APPLY_STATUS.SENDING;
    }
  })();

  return (
    <Card elevation={3} className={container}>
      <CardContent className={content}>
        <Typography variant="subtitle2" className={codeStyle}>
          {`Loyalty code #${code}`}
        </Typography>
        <Button
          variant="text"
          size="small"
          className={buttonStyle}
          onClick={removeLoyalty}
        >
          Remove
        </Button>
      </CardContent>
      <LoyaltyApplyStatus status={statusType} errorText={NOTFOUND_ERROR} />
    </Card>
  );
};

export default LoyaltiesItemReview;
